export function StressAndLifestyleImpact() {
  return (
    <section className="py-20 bg-gradient-to-br from-red-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The Hidden Cost: Your Time, Stress, and Quality of Life
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Financial calculations don't capture the real human cost of managing a major apartment building renovation. Here's what owners actually experience.
          </p>
        </div>

        {/* Stress Factors */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
            <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Time Commitment</h3>
            <p className="text-gray-700 mb-4">
              Expect to spend 10-20 hours per week managing contractors, reviewing progress, handling issues, and making decisions.
            </p>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm font-bold text-red-700">Over 18 months: 780-1,560 hours</p>
              <p className="text-sm text-gray-700">That's equivalent to a full-time job for 5-10 months</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
            <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-phone-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Constant Interruptions</h3>
            <p className="text-gray-700 mb-4">
              Daily calls and texts from contractors, suppliers, inspectors, and tenants. Your phone never stops ringing.
            </p>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm font-bold text-red-700">Average: 5-10 calls/texts per day</p>
              <p className="text-sm text-gray-700">Disrupts your work, family time, and peace of mind</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
            <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-alert-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Crisis Management</h3>
            <p className="text-gray-700 mb-4">
              Unexpected problems arise constantly: contractor no-shows, material delays, permit issues, weather delays, and surprise repairs.
            </p>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm font-bold text-red-700">Expect: 2-3 major crises per month</p>
              <p className="text-sm text-gray-700">Each requiring immediate attention and problem-solving</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
            <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Financial Anxiety</h3>
            <p className="text-gray-700 mb-4">
              Constant worry about cost overruns, whether the investment will pay off, and if you'll run out of money mid-project.
            </p>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm font-bold text-red-700">Sleepless nights worrying about money</p>
              <p className="text-sm text-gray-700">Stress affects health, relationships, and well-being</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
            <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-team-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Relationship Strain</h3>
            <p className="text-gray-700 mb-4">
              The stress and time commitment of managing renovations puts strain on marriages, family relationships, and friendships.
            </p>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm font-bold text-red-700">Missed family events and quality time</p>
              <p className="text-sm text-gray-700">Partners and children feel neglected and frustrated</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
            <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-mental-health-line text-2xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Health Impact</h3>
            <p className="text-gray-700 mb-4">
              Chronic stress leads to high blood pressure, poor sleep, weight gain, and increased risk of serious health problems.
            </p>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <p className="text-sm font-bold text-red-700">Physical and mental health decline</p>
              <p className="text-sm text-gray-700">Medical costs and reduced quality of life</p>
            </div>
          </div>
        </div>

        {/* Real Owner Testimonials */}
        <div className="bg-white rounded-2xl p-10 mb-12 shadow-xl border-2 border-red-300">
          <h3 className="text-3xl font-bold mb-8 text-center">What Owners Actually Say</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <div className="flex items-start gap-4 mb-4">
                <i className="ri-double-quotes-l text-3xl text-red-600"></i>
                <div>
                  <p className="text-gray-700 italic mb-4">
                    "I thought I could handle the renovation myself. It consumed my life for 14 months. I missed my daughter's graduation, my marriage nearly fell apart, and I developed anxiety issues. Looking back, the extra money wasn't worth what it cost me personally."
                  </p>
                  <p className="font-bold text-gray-800">- Michael T., Phoenix</p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <div className="flex items-start gap-4 mb-4">
                <i className="ri-double-quotes-l text-3xl text-red-600"></i>
                <div>
                  <p className="text-gray-700 italic mb-4">
                    "Every day brought a new problem. I couldn't focus at my day job, I was constantly stressed, and I gained 30 pounds from stress eating. When I finally sold, I realized I would have been better off selling AS IS and avoiding the whole nightmare."
                  </p>
                  <p className="font-bold text-gray-800">- Jennifer L., Dallas</p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <div className="flex items-start gap-4 mb-4">
                <i className="ri-double-quotes-l text-3xl text-red-600"></i>
                <div>
                  <p className="text-gray-700 italic mb-4">
                    "The financial loss was bad enough, but the real cost was 18 months of my life I'll never get back. I was miserable, my family was miserable, and for what? I should have just sold AS IS from the beginning."
                  </p>
                  <p className="font-bold text-gray-800">- Robert K., Indianapolis</p>
                </div>
              </div>
            </div>

            <div className="bg-teal-50 rounded-xl p-6 border border-teal-200">
              <div className="flex items-start gap-4 mb-4">
                <i className="ri-double-quotes-l text-3xl text-teal-600"></i>
                <div>
                  <p className="text-gray-700 italic mb-4">
                    "Best decision I ever made was selling AS IS. I got my cash in 3 weeks, reinvested it immediately, and moved on with my life. No stress, no headaches, no regrets. I'm actually enjoying life again instead of being a slave to a renovation project."
                  </p>
                  <p className="font-bold text-gray-800">- Sarah M., Atlanta</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* The Choice */}
        <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-10 text-center border-2 border-teal-200">
          <h3 className="text-3xl font-bold mb-6">You Have a Choice</h3>
          <p className="text-xl text-gray-800 mb-8 max-w-3xl mx-auto leading-relaxed">
            You can spend the next 12-18 months stressed, overwhelmed, and watching your quality of life deteriorate while managing a renovation that may not even pay off...
          </p>
          <p className="text-2xl font-bold text-teal-600 mb-8">
            Or you can sell AS IS, get cash in 7-30 days, and move on with your life.
          </p>
          <p className="text-lg text-gray-700">
            Which sounds better to you?
          </p>
        </div>
      </div>
    </section>
  );
}